import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Btn } from './primitives';

/** Default ring diameter (px) for button loading indicators. */
export const DEFAULT_BUTTON_SPINNER_SIZE = 14;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Ring = styled.span`
  display: block;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: 2px solid color-mix(in oklch, currentColor 22%, transparent);
  border-top-color: currentColor;
  border-radius: 50%;
  box-sizing: border-box;
  animation: ${spin} 0.7s linear infinite;
`;

const Slot = styled.span`
  display: grid;
  place-items: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  contain: layout style paint;
`;

/**
 * CSS ring spinner for button loading — fixed box; rotate transform does not
 * shift layout like MUI CircularProgress SVG stroke animation.
 */
export function ButtonSpinner({ size = DEFAULT_BUTTON_SPINNER_SIZE, className }) {
  return <Ring $size={size} className={className} />;
}

/**
 * Fixed-size slot wrapping {@link ButtonSpinner} — keeps Btn geometry stable while loading.
 */
export function ButtonSpinnerSlot({
  size = DEFAULT_BUTTON_SPINNER_SIZE,
  'aria-hidden': ariaHidden = 'true',
  ...rest
}) {
  return (
    <Slot $size={size} aria-hidden={ariaHidden} {...rest}>
      <ButtonSpinner size={size} />
    </Slot>
  );
}

/**
 * Portal Btn that swaps label for a centered spinner when loading.
 * Parent owns fetch/state and passes `loading` + handlers unchanged.
 */
export function BtnWithLoading({
  loading = false,
  children,
  spinnerSize = DEFAULT_BUTTON_SPINNER_SIZE,
  loadingAriaLabel,
  disabled,
  ...btnProps
}) {
  const isBusy = Boolean(loading);

  return (
    <Btn
      {...btnProps}
      disabled={disabled || isBusy}
      aria-busy={isBusy || undefined}
      aria-label={isBusy && loadingAriaLabel ? loadingAriaLabel : btnProps['aria-label']}
    >
      {isBusy ? <ButtonSpinnerSlot size={spinnerSize} /> : children}
    </Btn>
  );
}
